import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Box,
  RadioGroup,
  Radio,
  FormControlLabel
} from '@mui/material';
import StyledFlatpickr from 'components/StyledFlatpickr';

export const TimeRangeSection = ({ 
  formik, 
  loading, 
  hasNestedError, 
  getNestedErrorMessage,
  timeRangeModes,
  timeUnits,
  timeGranularities
}) => {
  return (
    <Grid container spacing={2}>
      {/* Time Range Mode */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          What time period of data should be fetched?
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            name="dataTimeConfig.mode"
            value={formik.values.dataTimeConfig.mode}
            onChange={(e) => {
              const newMode = e.target.value;
              formik.setFieldValue('dataTimeConfig.mode', newMode);
              
              // Set default values when switching modes
              if (newMode === 'RELATIVE') {
                formik.setFieldValue('dataTimeConfig.relativeValue', 1);
                formik.setFieldValue('dataTimeConfig.relativeUnit', 'DAY');
                formik.setFieldValue('dataTimeConfig.startDateTime', null);
                formik.setFieldValue('dataTimeConfig.endDateTime', null);
              } else if (newMode === 'ABSOLUTE') {
                formik.setFieldValue('dataTimeConfig.relativeValue', null);
                formik.setFieldValue('dataTimeConfig.relativeUnit', null);
              }
            }}
          >
            <FormControlLabel 
              value="RELATIVE" 
              control={<Radio />} 
              label="Last X Days/Weeks/Months" 
              disabled={loading}
            />
            <FormControlLabel 
              value="ABSOLUTE" 
              control={<Radio />} 
              label="Specific Date Range" 
              disabled={loading}
            />
          </RadioGroup>
          {hasNestedError(formik, 'dataTimeConfig', 'mode') && (
            <FormHelperText error>
              {getNestedErrorMessage(formik, 'dataTimeConfig', 'mode')}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
      
      {/* Conditional fields based on mode */}
      {formik.values.dataTimeConfig.mode === 'RELATIVE' && (
        <RelativeTimeConfig 
          formik={formik}
          loading={loading}
          hasNestedError={hasNestedError}
          getNestedErrorMessage={getNestedErrorMessage}
          timeUnits={timeUnits}
        />
      )}
      
      {formik.values.dataTimeConfig.mode === 'ABSOLUTE' && (
        <AbsoluteTimeConfig 
          formik={formik}
          loading={loading}
          hasNestedError={hasNestedError}
          getNestedErrorMessage={getNestedErrorMessage}
        />
      )}
      
      {/* Granularity selection available for both modes */}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Data Granularity</InputLabel>
          <Select
            name="dataTimeConfig.granularity"
            value={formik.values.dataTimeConfig.granularity || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Data Granularity"
            disabled={loading}
          >
            {timeGranularities.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Select how detailed the data should be (hourly, daily, etc.)
          </FormHelperText>
        </FormControl>
      </Grid>
    </Grid>
  );
};

/**
 * Relative time configuration component with improved user experience
 */
const RelativeTimeConfig = ({ 
  formik, 
  loading,
  hasNestedError,
  getNestedErrorMessage,
  timeUnits
}) => {
  return (
    <Grid item xs={12} sx={{ pl: 4, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body1">
          Fetch data from the last
        </Typography>
        <TextField
          name="dataTimeConfig.relativeValue"
          type="number"
          value={formik.values.dataTimeConfig.relativeValue || ''}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={hasNestedError(formik, 'dataTimeConfig', 'relativeValue')}
          inputProps={{ 
            min: 1,
            style: { textAlign: 'center' }
          }}
          sx={{ width: '80px' }}
          disabled={loading}
        />
        <FormControl sx={{ minWidth: 120 }}>
          <Select
            name="dataTimeConfig.relativeUnit"
            value={formik.values.dataTimeConfig.relativeUnit || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={hasNestedError(formik, 'dataTimeConfig', 'relativeUnit')}
            disabled={loading}
            displayEmpty
          >
            {timeUnits.map((unit) => (
              <MenuItem key={unit.value} value={unit.value}>
                {unit.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {(hasNestedError(formik, 'dataTimeConfig', 'relativeValue') || 
       hasNestedError(formik, 'dataTimeConfig', 'relativeUnit')) && (
        <FormHelperText error sx={{ pl: 2 }}>
          {getNestedErrorMessage(formik, 'dataTimeConfig', 'relativeValue') || 
           getNestedErrorMessage(formik, 'dataTimeConfig', 'relativeUnit')}
        </FormHelperText>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pl: 2 }}>
        Example: "Last 7 Days" will fetch data from the past week relative to when the job runs
      </Typography>
    </Grid>
  );
};

/**
 * Absolute time configuration component with improved date pickers
 */
const AbsoluteTimeConfig = ({ 
  formik, 
  loading,
  hasNestedError,
  getNestedErrorMessage
}) => {
  return (
    <Grid item xs={12} sx={{ pl: 4 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <StyledFlatpickr
            label="Start Date & Time"
            value={formik.values.dataTimeConfig.startDateTime ? new Date(formik.values.dataTimeConfig.startDateTime) : null}
            onChange={(selectedDates) => {
              formik.setFieldValue('dataTimeConfig.startDateTime', 
                selectedDates && selectedDates[0] ? selectedDates[0].toISOString() : null);
            }}
            options={{
              enableTime: true,
              dateFormat: "Y-m-d H:i",
              time_24hr: true
            }}
            disabled={loading}
            error={hasNestedError(formik, 'dataTimeConfig', 'startDateTime')}
            helperText={hasNestedError(formik, 'dataTimeConfig', 'startDateTime')
              ? getNestedErrorMessage(formik, 'dataTimeConfig', 'startDateTime')
              : "When to start fetching data from"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StyledFlatpickr
            label="End Date & Time"
            value={formik.values.dataTimeConfig.endDateTime ? new Date(formik.values.dataTimeConfig.endDateTime) : null}
            onChange={(selectedDates) => {
              formik.setFieldValue('dataTimeConfig.endDateTime', 
                selectedDates && selectedDates[0] ? selectedDates[0].toISOString() : null);
            }}
            options={{
              enableTime: true,
              dateFormat: "Y-m-d H:i",
              time_24hr: true
            }}
            disabled={loading}
            error={hasNestedError(formik, 'dataTimeConfig', 'endDateTime')}
            helperText={hasNestedError(formik, 'dataTimeConfig', 'endDateTime')
              ? getNestedErrorMessage(formik, 'dataTimeConfig', 'endDateTime')
              : "When to stop fetching data"}
          />
        </Grid>
      </Grid>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Example: From "January 1, 2025 00:00" to "January 31, 2025 23:59" will fetch the entire month of January
      </Typography>
    </Grid>
  );
};